/**
 * PostContent Component
 *
 * Renders a post's HTML body with its image markers replaced by actual
 * images. Each marker carries a stable `data-image-id` so images resolve to
 * their body position by identity (see `@letsgethome/lib/blog`), not by
 * counting placeholders — the coupling that used to scramble images whenever
 * the body was edited. Legacy attribute-less bodies still render via the
 * resolver's ordinal fallback until the backfill re-anchors them.
 *
 * Smart sizing: Images are rendered full-width only if they have sufficient
 * resolution (>= 768px). Smaller images are centered at their natural size
 * to avoid blurry upscaling.
 */
import { reconcileBodyImages } from '@letsgethome/lib/blog';
import {
  plainText,
  type ImageId,
  type S3Key,
  type SafeHtmlString,
} from '@letsgethome/types/branded';
import { S3Image } from '@letsgethome/ui/s3-image';
import { SafeHtml } from '@letsgethome/ui/safe-html';
import { useMemo } from 'react';

/** Threshold width for full-width display (matches article max-width) */
const FULL_WIDTH_THRESHOLD = 768;

interface PostImage {
  id: ImageId;
  key: S3Key;
  filename: string;
  sortOrder: number;
  /** Original width - if >= 768, display full width; else natural size centered */
  width?: number;
  /** Original height - used for aspect ratio */
  height?: number;
}

interface PostContentProps {
  /** Pre-sanitized HTML body with <img> placeholders */
  body: SafeHtmlString;
  /** Images to insert at placeholder positions */
  images: PostImage[];
  /** Alt text base (usually post title) */
  altText: string;
  /** Partner logo S3 key for overlay on images (optional) */
  partnerLogoHorizontalKey?: S3Key;
  partnerName?: string;
}

/**
 * Renders a single post image with smart sizing.
 * - Large images (>= 768px): Full width, responsive
 * - Small images (< 768px): Natural size, centered (avoid blur)
 */
function PostImageComponent({
  image,
  altText,
  index,
  partnerLogoHorizontalKey,
  partnerName,
}: {
  image: PostImage;
  altText: string;
  index: number;
  partnerLogoHorizontalKey?: S3Key;
  partnerName?: string;
}) {
  const isFullWidth = !image.width || image.width >= FULL_WIDTH_THRESHOLD;

  if (isFullWidth) {
    // Full-width: Let Next.js handle responsive sizing
    return (
      <figure className="relative my-8">
        <S3Image
          src={image.key}
          alt={plainText(`${altText} - ${index + 1}`)}
          width={image.width || 1200}
          height={image.height || 800}
          className="block h-auto w-full"
          loading={index === 0 ? 'eager' : 'lazy'}
          sizes="(max-width: 768px) 100vw, 768px"
          style={{ width: '100%', height: 'auto' }}
        />
        {partnerLogoHorizontalKey && (
          <S3Image
            src={partnerLogoHorizontalKey}
            alt={plainText(partnerName ?? '')}
            width={144}
            height={48}
            className="absolute left-0 top-0 h-12 w-auto"
            style={{ height: '48px', width: 'auto' }}
          />
        )}
      </figure>
    );
  }

  // Small image: Render at natural size, centered
  return (
    <figure className="relative my-8 flex justify-center">
      <div className="relative">
        <S3Image
          src={image.key}
          alt={plainText(`${altText} - ${index + 1}`)}
          width={image.width ?? 800}
          height={image.height ?? 600}
          className="block h-auto"
          loading={index === 0 ? 'eager' : 'lazy'}
          style={{ maxWidth: '100%', height: 'auto' }}
        />
        {partnerLogoHorizontalKey && (
          <S3Image
            src={partnerLogoHorizontalKey}
            alt={plainText(partnerName ?? '')}
            width={144}
            height={48}
            className="absolute left-0 top-0 h-12 w-auto"
            style={{ height: '48px', width: 'auto' }}
          />
        )}
      </div>
    </figure>
  );
}

/**
 * Renders a post body, replacing each image marker with its image resolved
 * by identity. The ordering + fallback logic lives in the framework-free
 * `reconcileBodyImages` (`@letsgethome/lib/blog`); this component only maps
 * the resulting segments to React.
 */
export function PostContent({
  body,
  images,
  altText,
  partnerLogoHorizontalKey,
  partnerName,
}: PostContentProps) {
  const processedContent = useMemo(() => {
    const segments = reconcileBodyImages<PostImage>(body, images);
    let imageIndex = 0;
    return segments.map((segment, i) => {
      if (segment.kind === 'html') {
        // Safe cast: a segment is a substring of the pre-sanitized body.
        return <SafeHtml key={`html-${i}`} content={segment.html as SafeHtmlString} />;
      }
      const index = imageIndex++;
      return (
        <PostImageComponent
          key={`img-${segment.image.id}`}
          image={segment.image}
          altText={altText}
          index={index}
          partnerLogoHorizontalKey={partnerLogoHorizontalKey}
          partnerName={partnerName}
        />
      );
    });
  }, [body, images, altText, partnerLogoHorizontalKey, partnerName]);

  return (
    <div className="prose max-w-none [&_h2]:scroll-mt-24 [&_h3]:scroll-mt-24">
      {processedContent}
    </div>
  );
}
